import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import ObsidianAuraBackground from "../Components/ObsidianAuraBackground";
import ContactForm from "../Components/ContactForm";

import "../Style/ContactPremium.css";
import "../Style/Accueil.ObsidianAura.css";

const Contact = () => {
  const { t } = useTranslation("contact");

  const sendEmail = async (formData) => {
    const payload = {
      ...formData,
      source: "contact_demo",
      page: "/contact",
    };

    const endpoint =
      process.env.REACT_APP_CONTACT_API_URL ||
      "https://osqvaiaq1d.execute-api.us-east-1.amazonaws.com/prod/send-email";

    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = t("contactForm.messages.genericError");

      try {
        const errorPayload = await response.clone().json();
        if (errorPayload?.detail || errorPayload?.message) {
          errorMessage = errorPayload.detail || errorPayload.message;
        }
      } catch (_) {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(errorMessage);
    }

    return response.json().catch(() => ({}));
  };

  const relationOptions = [
    { value: "Demo", text: t("relationOptions.0") },
    { value: "Pricing", text: t("relationOptions.1") },
    { value: "Support", text: t("relationOptions.2") },
    { value: "Press", text: t("relationOptions.3") },
    { value: "Other", text: t("relationOptions.4") },
  ];

  return (
    <main className="life-contact life-contact--premium">
      <Helmet>
        <title>{t("app.title")}</title>
        <meta name="title" content={t("app.metaTitle")} />
        <meta name="description" content={t("app.metaDescription")} />
        <link rel="canonical" href="https://be-lifer.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t("app.metaTitle")} />
        <meta property="og:description" content={t("app.metaDescription")} />
        <meta property="og:url" content="https://be-lifer.com/contact" />
        <meta property="og:site_name" content="Be-Lifer" />
      </Helmet>

      <ObsidianAuraBackground intensity={0.95} speed={0.22} />
      <div className="life-contact__overlay" />

      <section className="life-contact-hero">
        <div className="life-contact-hero__inner">
          <div className="life-contact-hero__left">
            <div className="life-contact-kicker">{t("kicker")}</div>
            <h1 className="life-contact-title">{t("title")}</h1>
            <p className="life-contact-lead">{t("lead")}</p>

            <div className="life-contact-badges" aria-label={t("badgesLabel")}>
              <span>{t("badges.0")}</span>
              <span>{t("badges.1")}</span>
              <span>{t("badges.2")}</span>
            </div>
          </div>

          <div className="life-contact-hero__right">
            <div className="life-contact-card">
              <div className="life-contact-card__title">{t("formTitle")}</div>
              <div className="life-contact-card__subtitle">{t("formSubtitle")}</div>

              <ContactForm
                defaultMessagePlaceholder={t("formPlaceholder")}
                defaultRelation={t("formDefaultRelation")}
                options={relationOptions}
                sendEmail={sendEmail}
              />

              <div className="life-contact-footnote">{t("footnote")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="life-contact-mini">
        <div className="life-contact-mini__inner">
          <h2 className="life-contact-mini__title">{t("miniTitle")}</h2>
          <p className="life-contact-mini__text">{t("miniText")}</p>
        </div>
      </section>
    </main>
  );
};

export default Contact;
